import { Filter } from '@vrplatform/components';
import { useAccountsQuery, useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { SelectItem } from '@vrplatform/components';
import { type Maybe, toTitleCase } from '@vrplatform/utils';
import { useMemo } from 'react';

type Props = {
  value: Maybe<string[]>;
  setValue: (value: Maybe<string[]>) => void;
  view: 'all' | 'pm-statement';
};

function formatAccount(
  account: NonNullable<ReturnType<typeof useAccountsQuery>['data']>[number]
): SelectItem {
  return {
    value: account.id,
    label: account.name || 'No name',
    group: toTitleCase(account.category?.classification || '-'),
  };
}

function useJournalEntryAccountFilterQuery({ view }: { view: Props['view'] }) {
  const [{ id: teamId }] = useTeam();

  const queryData = useAccountsQuery({
    skip: !teamId,
  });

  const data = useMemo(() => {
    if (view === 'all') {
      return queryData.data?.map(formatAccount);
    }

    return queryData.data
      ?.filter(
        (item) =>
          item.category?.classification !== 'asset' &&
          item.category?.classification !== 'liability'
      )
      .map(formatAccount);
  }, [queryData.data, view]);

  return {
    ...queryData,
    data,
  };
}

export const ACCOUNT_UNMAPPED_OPTION: SelectItem = {
  label: 'Unmapped',
  value: 'unmapped',
  icon: <Icon icon="MinusCircleIcon" size={16} />,
};

export const JournalEntryAccountFilter = ({
  value: initial,
  setValue,
  view,
}: Props) => {
  'use memo';

  const queryData = useJournalEntryAccountFilterQuery({
    view,
  });

  const value: SelectItem[] = useMemo(
    () =>
      initial?.includes(ACCOUNT_UNMAPPED_OPTION.value)
        ? [ACCOUNT_UNMAPPED_OPTION]
        : queryData.data?.filter((item) => initial?.includes(item.value)) || [],
    [initial, queryData.data]
  );

  const bottomActions = useMemo(
    () =>
      view === 'all'
        ? [
            {
              id: ACCOUNT_UNMAPPED_OPTION.value,
              label: ACCOUNT_UNMAPPED_OPTION.label,
              onSubmit: () => {
                setValue([ACCOUNT_UNMAPPED_OPTION.value]);
              },
              icon: ACCOUNT_UNMAPPED_OPTION.icon,
            },
          ]
        : undefined,
    [setValue, view]
  );

  return (
    <Filter.Select
      value={value}
      multiLabel="accounts"
      setValue={(newValues) => {
        if (!newValues.length) {
          setValue(null);
        } else {
          setValue(
            newValues
              .map((v) => v.value)
              .filter((x) => x !== ACCOUNT_UNMAPPED_OPTION.value)
          );
        }
      }}
      type="multiple"
      label="Account"
      withinPortal
      hideIcon
      data={{
        options: queryData.data || [],
        loading: queryData.isLoading,
        error: queryData.error,
      }}
      customBottomActions={bottomActions}
    />
  );
};
